import { useEffect, useState } from 'react';
import { Heart, Sparkles, Star, Coins as CoinsIcon, Gift, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';

interface TipCelebrationProps {
  amount: number;
  userName: string;
  onComplete: () => void;
}

export function TipCelebration({ amount, userName, onComplete }: TipCelebrationProps) {
  const [isAnimating, setIsAnimating] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsAnimating(false);
      onComplete();
    }, 4000);

    return () => clearTimeout(timer);
  }, [onComplete]);

  // Determine celebration tier based on amount
  const celebrationTier = amount >= 500 ? 'mega' : amount >= 100 ? 'large' : 'normal';
  
  // Create multiple types of confetti for richer animation
  const confettiCount = Math.min(Math.floor(amount / 5), 50);
  const confettiElements = Array.from({ length: confettiCount }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 0.8,
    duration: 2 + Math.random() * 2,
    rotation: Math.random() * 360,
    icon: [Heart, Star, CoinsIcon, Sparkles, Gift][Math.floor(Math.random() * 5)],
  }));

  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {/* Animated background pulse for mega tips */}
      {celebrationTier === 'mega' && (
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-accent/20 to-primary/20 animate-pulse" />
      )}

      {/* Confetti rain with multiple icons */}
      {confettiElements.map((confetti) => {
        const Icon = confetti.icon;
        return (
          <div
            key={confetti.id}
            className="absolute -top-10 animate-fade-out"
            style={{
              left: `${confetti.left}%`,
              animationDelay: `${confetti.delay}s`,
              animationDuration: `${confetti.duration}s`,
              transform: `rotate(${confetti.rotation}deg)`,
            }}
          >
            <Icon 
              className={cn(
                "animate-bounce",
                celebrationTier === 'mega' ? "h-8 w-8 text-yellow-400" :
                celebrationTier === 'large' ? "h-6 w-6 text-primary" :
                "h-4 w-4 text-accent"
              )}
              fill="currentColor"
              style={{
                filter: 'drop-shadow(0 0 8px currentColor)',
              }}
            />
          </div>
        );
      })}

      {/* Particle bursts from sides (TikTok-style) */}
      {celebrationTier !== 'normal' && (
        <>
          <div className="absolute left-0 top-1/2 -translate-y-1/2 animate-slide-in-right">
            <div className="flex flex-col gap-2">
              {[...Array(5)].map((_, i) => (
                <Zap 
                  key={`left-${i}`}
                  className="h-6 w-6 text-primary animate-pulse"
                  style={{ animationDelay: `${i * 0.1}s` }}
                />
              ))}
            </div>
          </div>
          <div className="absolute right-0 top-1/2 -translate-y-1/2 animate-slide-in-left">
            <div className="flex flex-col gap-2">
              {[...Array(5)].map((_, i) => (
                <Zap 
                  key={`right-${i}`}
                  className="h-6 w-6 text-accent animate-pulse"
                  style={{ animationDelay: `${i * 0.1}s` }}
                />
              ))}
            </div>
          </div>
        </>
      )}

      {/* Center celebration message with enhanced styling */}
      <div className={cn(
        "absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center",
        "animate-scale-in"
      )}>
        <div className={cn(
          "bg-gradient-to-br backdrop-blur-md rounded-3xl px-10 py-8 shadow-2xl border-4 relative overflow-hidden",
          celebrationTier === 'mega' 
            ? "from-yellow-500/95 via-orange-500/95 to-red-500/95 border-yellow-300/50 scale-110" 
            : celebrationTier === 'large'
            ? "from-primary/95 to-accent/95 border-white/30 scale-105"
            : "from-primary/90 to-accent/90 border-white/20"
        )}>
          {/* Animated shine effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-slide-in-right" />
          
          <div className="relative z-10">
            <div className="flex items-center justify-center gap-3 mb-3">
              <Gift className={cn(
                "animate-bounce text-white",
                celebrationTier === 'mega' ? "h-10 w-10" : "h-8 w-8"
              )} />
              <Sparkles className={cn(
                "animate-pulse text-white",
                celebrationTier === 'mega' ? "h-8 w-8" : "h-6 w-6"
              )} />
            </div>
            
            <p className={cn(
              "font-black text-white mb-2 drop-shadow-lg",
              celebrationTier === 'mega' ? "text-5xl" : 
              celebrationTier === 'large' ? "text-4xl" : "text-3xl"
            )}>
              {amount} COINS!
            </p>
            
            <div className="flex items-center justify-center gap-2">
              <Heart className="h-5 w-5 text-white/90 animate-pulse" fill="currentColor" />
              <p className="text-white/90 font-semibold text-lg">
                {userName} just gifted!
              </p>
              <Heart className="h-5 w-5 text-white/90 animate-pulse" fill="currentColor" />
            </div>
            
            {celebrationTier === 'mega' && (
              <p className="text-white/80 text-sm mt-2 font-bold uppercase tracking-wider">
                🎉 MEGA GIFT! 🎉
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Bottom notification badge (TikTok-style) */}
      <div className="absolute bottom-20 left-1/2 -translate-x-1/2 animate-fade-in">
        <div className="bg-background/95 backdrop-blur-sm rounded-full px-6 py-3 shadow-xl border border-border">
          <div className="flex items-center gap-2">
            <CoinsIcon className="h-5 w-5 text-primary" />
            <span className="text-sm font-semibold">+{amount} to creator</span>
          </div>
        </div>
      </div>
    </div>
  );
}
